// components/EditableText.js

import React, { useState, useRef, useEffect } from 'react';
import { Text } from 'react-konva';

const EditableText = ({
  x,
  y,
  text,
  fontSize = 16,
  fontFamily = 'Arial',
  fontStyle = 'normal',
  align = 'left',
  lineHeight = 1.2,
  fill = 'red',
  rotation = 0,
  onChange,
  onChangeLive = true,
  isEditable = true,
  ignoreStageScale = false,
  stageScale = 1,
  ...rest // Принимаем остальные свойства
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const textRef = useRef();
  const inputRef = useRef(null);
  const onChangeRef = useRef(onChange);
  const initValueAppliedRef = useRef(false);

  // Держим актуальный onChange без перезапуска эффекта редактирования
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  // Создание/удаление input только при начале/окончании редактирования
  useEffect(() => {
    if (isEditing && textRef.current) {
      const textNode = textRef.current;
      const stage = textNode.getStage();
      const stageBox = stage.container().getBoundingClientRect();
      const absScale = textNode.getAbsoluteScale();
      const sizeScaleY = ignoreStageScale ? 1 : (absScale ? absScale.y : 1);
      // Надёжно вычисляем экранный прямоугольник текста с учётом всех трансформаций
      const clientRect = textNode.getClientRect({ skipShadow: true, skipStroke: true });
      const nodeOffsetX = textNode.offsetX() || 0;
      const nodeOffsetY = textNode.offsetY() || 0;
      const usesOffset = nodeOffsetX !== 0 || nodeOffsetY !== 0;
      const rectLeft = stageBox.left + clientRect.x;
      const rectTop = stageBox.top + clientRect.y;
      const rectWidth = clientRect.width;
      const rectHeight = clientRect.height;
      // Изначально ставим textarea по клиентскому прямоугольнику
      const areaPosition = usesOffset
        ? {
            x: rectLeft + rectWidth / 2 - rectWidth / 2,
            y: rectTop + rectHeight / 2 - rectHeight / 2,
          }
        : { x: rectLeft, y: rectTop };

      const input = document.createElement('textarea');
      document.body.appendChild(input);
      inputRef.current = input;
      // Устанавливаем исходный текст сразу, чтобы рамка была сразу по контенту
      input.value = text || '';
      initValueAppliedRef.current = true;

      let isRemoved = false; // Флаг для отслеживания удаления input

      input.style.position = 'absolute';
      input.style.top = areaPosition.y + 'px';
      input.style.left = areaPosition.x + 'px';
      input.style.width = rectWidth + 'px';
      input.style.height = rectHeight + 'px';
      input.style.minHeight = rectHeight + 'px';
      input.style.border = '1px solid red';
      input.style.boxSizing = 'content-box';
      input.style.padding = '0px';
      input.style.margin = '0px';
      input.style.overflow = 'hidden';
      input.style.overflowX = 'hidden';
      input.style.overflowY = 'hidden';
      input.style.background = 'white';
      input.style.outline = 'none';
      input.style.transformOrigin = usesOffset ? 'center center' : 'left top';
      // Текстовые стили и значение задаются отдельным эффектом ниже
      input.setAttribute('wrap', 'off');
      input.style.whiteSpace = 'pre';
      input.style.lineHeight = String(lineHeight || 1.2);
      input.style.resize = 'none';

      input.focus();

      // Блокируем горячие клавиши холста, пока введение текста активно
      const stopPropagation = (e) => {
        e.stopPropagation();
      };
      input.addEventListener('keydown', stopPropagation, true);
      input.addEventListener('keyup', stopPropagation, true);
      input.addEventListener('keypress', stopPropagation, true);

      const removeInput = () => {
        if (isRemoved) return; // Если input уже удалён, ничего не делаем
        isRemoved = true;

        try { 
          // Снимаем фокус с input ДО его удаления
          if (document.activeElement === input) {
            input.blur();
          }
          document.body.removeChild(input); 
          // Гарантируем, что фокус не останется на удаленном элементе
          setTimeout(() => {
            // ИСПРАВЛЕНИЕ: Принудительно возвращаем фокус на Stage
            const textNode = textRef.current;
            if (textNode) {
              try {
                const stage = textNode.getStage();
                if (stage && stage.container) {
                  const container = stage.container();
                  if (container && typeof container.focus === 'function') {
                    // Устанавливаем tabIndex, если его нет, чтобы контейнер мог получить фокус
                    if (!container.hasAttribute('tabindex')) {
                      container.setAttribute('tabindex', '0');
                    }
                    container.focus();
                  }
                }
              } catch (e) {
                console.warn('⚠️ Не удалось вернуть фокус на Stage:', e);
              }
            }
            
            // Дополнительная проверка
            if (!document.activeElement || document.activeElement === document.body) {
              // Фокус сброшен после редактирования текста
            }
          }, 0);
        } catch (_) {}
        inputRef.current = null;
        setIsEditing(false);
      };

      const handleKeyDown = function (e) {
        if (e.keyCode === 27) {
          // Escape
          removeInput();
        }
      };

      const handleBlur = function () {
        if (onChangeRef.current) onChangeRef.current(input.value);
        removeInput();
      };

      // Применяем текстовые стили ДО первичного измерения
      // (scaleX/scaleY уже вычислены выше для позиционирования)
      input.style.fontSize = (fontSize * sizeScaleY) + 'px';
      input.style.color = fill;
      input.style.textAlign = align || 'left';
      input.style.fontFamily = fontFamily || 'Arial';
      const isItalic = (fontStyle || '').toLowerCase().includes('italic');
      const isBold = (fontStyle || '').toLowerCase().includes('bold');
      input.style.fontStyle = isItalic ? 'italic' : 'normal';
      input.style.fontWeight = isBold ? 'bold' : 'normal';

      input.addEventListener('keydown', handleKeyDown);
      // Реальное время: подгоняем размеры рамки textarea под ввод (без переноса строк)
      const handleInput = function () {
        try {
          const styles = getComputedStyle(input);
          const font = `${styles.fontStyle !== 'normal' ? styles.fontStyle + ' ' : ''}${styles.fontWeight !== 'normal' ? styles.fontWeight + ' ' : ''}${styles.fontSize} ${styles.fontFamily}`;
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          ctx.font = font;
          const lines = (input.value || '').split('\n');
          let max = 1;
          for (let i = 0; i < lines.length; i++) {
            const w = ctx.measureText(lines[i]).width;
            if (w > max) max = w;
          }
          const newW = Math.ceil(max) + 6; // запас + границы
          const singleLinePx = parseFloat(styles.fontSize) * (parseFloat(input.style.lineHeight) || 1.2);
          const newH = Math.max(1, Math.round(lines.length * singleLinePx + 6));
          input.style.width = newW + 'px';
          input.style.height = newH + 'px';
          // Переобновляем позицию от clientRect, чтобы не «улетало» при zoom
          const liveRect = textNode.getClientRect({ skipShadow: true, skipStroke: true });
          if (usesOffset) {
            input.style.left = (stageBox.left + liveRect.x + liveRect.width / 2 - newW / 2) + 'px';
            input.style.top = (stageBox.top + liveRect.y + liveRect.height / 2 - newH / 2) + 'px';
          } else {
            input.style.left = (stageBox.left + liveRect.x) + 'px';
            input.style.top = (stageBox.top + liveRect.y) + 'px';
          }
          // Живое обновление текста — опционально
          if (onChangeLive && typeof onChangeRef.current === 'function') {
            onChangeRef.current(input.value);
          }
        } catch (_) {}
      };
      input.addEventListener('input', handleInput);
      input.addEventListener('blur', handleBlur);
      // Первичный перерасчёт размеров и установка каретки
      try {
        handleInput();
        input.select();
      } catch (_) {}

      // Очищаем обработчики событий при размонтировании или изменении зависимости
      return () => {
        input.removeEventListener('keydown', handleKeyDown);
        input.removeEventListener('blur', handleBlur);
        input.removeEventListener('input', handleInput);
        input.removeEventListener('keydown', stopPropagation, true);
        input.removeEventListener('keyup', stopPropagation, true);
        input.removeEventListener('keypress', stopPropagation, true);
        removeInput();
      };
    }
    // При завершении редактирования удаляем input, если остался
    return () => {
      if (!isEditing && inputRef.current) {
        try { document.body.removeChild(inputRef.current); } catch (_) {}
        inputRef.current = null;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing, lineHeight, ignoreStageScale, stageScale]);

  // Обновление размеров/позиции input при изменении текста (без пересоздания)
  useEffect(() => {
    if (!isEditing) return;
    const input = inputRef.current;
    const textNode = textRef.current;
    if (!input || !textNode) return;
    try {
      const stage = textNode.getStage();
      const stageBox = stage.container().getBoundingClientRect();
      // const stageScaleY = stage.scaleY ? stage.scaleY() : 1; // удалено: не используется
      const nodeOffsetX = textNode.offsetX() || 0;
      const nodeOffsetY = textNode.offsetY() || 0;
      const usesOffset = nodeOffsetX !== 0 || nodeOffsetY !== 0;

      const styles = getComputedStyle(input);
      const font = `${styles.fontStyle !== 'normal' ? styles.fontStyle + ' ' : ''}${styles.fontWeight !== 'normal' ? styles.fontWeight + ' ' : ''}${styles.fontSize} ${styles.fontFamily}`;
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      ctx.font = font;
      const lines = (input.value || '').split('\n');
      let max = 1;
      for (let i = 0; i < lines.length; i++) {
        const w = ctx.measureText(lines[i]).width;
        if (w > max) max = w;
      }
      const newW = Math.ceil(max) + 4;
      const singleLinePx = parseFloat(styles.fontSize) * (parseFloat(input.style.lineHeight) || 1.2);
      const newH = Math.max(1, Math.round(lines.length * singleLinePx + 4));
      input.style.width = newW + 'px';
      input.style.height = newH + 'px';
      // Поддерживаем совпадение с позицией через clientRect
      const liveRect2 = textNode.getClientRect({ skipShadow: true, skipStroke: true });
      if (usesOffset) {
        input.style.left = (stageBox.left + liveRect2.x + liveRect2.width / 2 - newW / 2) + 'px';
        input.style.top = (stageBox.top + liveRect2.y + liveRect2.height / 2 - newH / 2) + 'px';
      } else {
        input.style.left = (stageBox.left + liveRect2.x) + 'px';
        input.style.top = (stageBox.top + liveRect2.y) + 'px';
      }
    } catch (_) {}
  }, [isEditing, text, lineHeight, ignoreStageScale, stageScale]);

  // Обновление значения и стилей input при изменении свойств во время редактирования
  useEffect(() => {
    if (!isEditing) return;
    const input = inputRef.current;
    if (!input) return;
    if (!initValueAppliedRef.current) {
      input.value = text || '';
      initValueAppliedRef.current = true;
      try {
        input.select();
        // триггерим перерасчёт размеров
        input.dispatchEvent(new Event('input', { bubbles: true }));
      } catch (_) {}
    }
    const absScale2 = textRef.current?.getAbsoluteScale?.();
    const sizeScaleY = ignoreStageScale ? 1 : (absScale2 ? absScale2.y : 1);
    input.style.fontSize = (fontSize * sizeScaleY) + 'px';
    input.style.color = fill;
    input.style.transform = `rotate(${rotation}deg)`;
    input.style.textAlign = align || 'left';
    input.style.fontFamily = fontFamily || 'Arial';
    // Поддержка сочетаний 'bold', 'italic', 'bold italic'
    const isItalic = (fontStyle || '').toLowerCase().includes('italic');
    const isBold = (fontStyle || '').toLowerCase().includes('bold');
    input.style.fontStyle = isItalic ? 'italic' : 'normal';
    input.style.fontWeight = isBold ? 'bold' : 'normal';
    input.style.whiteSpace = 'pre-wrap';
    input.style.lineHeight = String(lineHeight || 1.2);
  }, [isEditing, text, fontSize, fontFamily, fontStyle, align, lineHeight, fill, rotation, ignoreStageScale, stageScale]);

  return (
    <Text
      x={x}
      y={y}
      text={text}
      fontSize={fontSize}
      fontFamily={fontFamily}
      fontStyle={fontStyle}
      align={align}
      lineHeight={lineHeight}
      verticalAlign="top"
      fill={fill}
      rotation={rotation}
      ref={textRef}
      perfectDrawEnabled={false}
      listening={true}
      onDblClick={() => {
        if (!isEditable) return;
        setIsEditing(true);
      }}
      onTap={() => {
        if (!isEditable) return;
        setIsEditing(true);
      }}
      onTouchStart={() => {
        if (!isEditable) return;
        setIsEditing(true);
      }}
      {...rest} // Передаем остальные свойства
    />
  );
};

export default EditableText;
